import React from 'react';
import { Table, Tr, Th, Thead, Tbody } from 'react-super-responsive-table';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { Modal, Alert } from 'react-bootstrap';

import { AiOutlinePlusCircle } from 'react-icons/ai';
import Moment from 'moment';
import ReactHtmlParser from 'react-html-parser';
import Button from '../button/button';
import IconButton from '../button/iconbutton';

import Installment from '../installment/installment';
import DatePicker from '../datepicker/datepicker';
import CurrencyInput from '../currencyinput/currencyinput';
import FrequencyPicker from '../frequencypicker/frequencypicker';
import { StoreContext } from '../../utils/store';
import { MULTIPLE_MAX_WARNING, PAYMENT_WARNING_INTRO } from '../../constants';

// eslint-disable-next-line no-unused-vars
import installmentcontainercss from './installmentcontainer.css';
import { resize } from '../../utils/calculate';

function InstallmentContainer(props) {
  const {
    installments,
    setInstallments,
    addInstallmentRange,
    getNextKey,
    finalDate,
    hasErrors,
    setValidationErrors,
    validationWarning,
    setMaximumWarning,
    // eslint-disable-next-line no-unused-vars
    validateCallback,
  } = { ...props };

  const {
    incurredDate,
    installmentRangeFirstDate: [
      installmentRangeFirstDate,
      setInstallmentRangeFirstDate,
    ],
    installmentRangeLastDate: [
      installmentRangeLastDate,
      setInstallmentRangeLastDate,
    ],
    installmentRangeAmount: [installmentRangeAmount, setInstallmentRangeAmount],
    installmentRangeFrequency: [
      installmentRangeFrequency,
      setInstallmentRangeFrequency,
    ],
  } = React.useContext(StoreContext);

  const [minimumDate] = [...incurredDate];

  const [showMultiple, setShowMultiple] = React.useState(false);

  const [rangeFirstDateErrorMsg, setRangeFirstDateErrorMsg] = React.useState();
  const [rangeLastDateErrorMsg, setRangeLastDateErrorMsg] = React.useState();

  const [rangeInstallmentAmountErrorMsg, setRangeInstallmentAmountErrorMsg] =
    React.useState();
  const [
    rangeInstallmentRangeFrequencyErrorMsg,
    setRangeInstallmentRangeFrequencyErrorMsg,
  ] = React.useState();

  React.useEffect(() => {
    resize();
  }, [installments, validationWarning]);

  const validateNewRange = () => {
    let valid = true;
    setRangeFirstDateErrorMsg('');
    setRangeLastDateErrorMsg('');
    setRangeInstallmentAmountErrorMsg('');
    setRangeInstallmentRangeFrequencyErrorMsg('');

    const rangeFirstDateMoment = new Moment(installmentRangeFirstDate);
    const rangeLastDateMoment = new Moment(installmentRangeLastDate);
    const minimumDateMoment = new Moment(minimumDate);

    // Dates must both be after incurred date
    if (
      rangeFirstDateMoment.isBefore(minimumDateMoment) ||
      rangeLastDateMoment.isBefore(minimumDateMoment)
    ) {
      if (rangeFirstDateMoment.isBefore(minimumDateMoment)) {
        setRangeFirstDateErrorMsg('Cannot be before incurred date');
      }
      if (rangeLastDateMoment.isBefore(minimumDateMoment)) {
        setRangeLastDateErrorMsg('Cannot be before incurred date');
      }
      valid = false;
    } else {
      // Only check further date validation if dates are after incurred date
      if (rangeLastDateMoment.isBefore(rangeFirstDateMoment)) {
        setRangeLastDateErrorMsg('Cannot be before first payment date');
        setRangeFirstDateErrorMsg('Cannot be after last payment date');
        valid = false;
      }

      if (rangeFirstDateMoment.isSame(rangeLastDateMoment)) {
        setRangeFirstDateErrorMsg('Cannot be the same as last payment date');
        setRangeLastDateErrorMsg('Cannot be the same as first payment date');
        valid = false;
      }
    }

    if (!installmentRangeAmount) {
      setRangeInstallmentAmountErrorMsg('Amount required');
      valid = false;
    }

    if (!installmentRangeFrequency) {
      setRangeInstallmentRangeFrequencyErrorMsg('Frequency required');
      valid = false;
    }

    if (!installmentRangeFirstDate) {
      setRangeFirstDateErrorMsg('Date required');
      valid = false;
    }

    if (!installmentRangeLastDate) {
      setRangeLastDateErrorMsg('Date required');
      valid = false;
    }

    return valid;
  };

  const addItem = () => {
    const key = getNextKey(installments);
    const inst = {
      id: key,
      date: undefined,
      amount: undefined,
      key,
      dateErrorMessage: undefined,
      amountErrorMessage: undefined,
      sort: key,
    };
    setInstallments([...installments, inst]);
  };

  const updateItem = inst => {
    const item = installments.find(x => x.id === inst.id);
    if (!item) {
      const newInstallments = [inst, ...installments].sort(
        (a, b) => a.sort - b.sort
      );
      setInstallments(newInstallments);
    } else {
      const newInstallments = [
        inst,
        ...installments.filter(x => x.id !== inst.id),
      ].sort((a, b) => a.sort - b.sort);
      setInstallments(newInstallments);
    }
  };

  // Remove an installment from the list by its ID
  const removeItem = id => {
    setInstallments([
      ...installments.filter(installment => installment.id !== id),
    ]);
  };

  // Clear installments list and set totalAmount to 0
  const clearItems = () => {
    setShowMultiple(false);
    setInstallments([]);
    setValidationErrors([]);
    setMaximumWarning(false);
  };

  const clearRange = () => {
    setInstallmentRangeFirstDate(undefined);
    setInstallmentRangeAmount(undefined);
    setInstallmentRangeFrequency(undefined);
    setInstallmentRangeLastDate(undefined);
  };

  const warning = () => {
    if (validationWarning) {
      const itemList = [];
      validationWarning.forEach((item, index) => {
        const key = 'warning' + index;
        itemList.push(<li key={key}>{item}</li>);
      });
      return (
        <Alert key="warnings" variant="warning">
          {ReactHtmlParser(PAYMENT_WARNING_INTRO)}
          <ul>{itemList}</ul>
        </Alert>
      );
    }
    return null;
  };

  const multiplePayments = () => {
    return (
      <Modal
        show={showMultiple}
        onHide={() => {
          setShowMultiple(false);
        }}
        aria-labelledby="modal-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="modal-title">Add multiple payments</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="no-margin multi-payment__fields">
            <Col className="min-margin multi-payment__field">
              <DatePicker
                title="First payment date*"
                date={installmentRangeFirstDate}
                minDate={minimumDate}
                maxDate={finalDate}
                onDateChange={date => {
                  setRangeFirstDateErrorMsg('');
                  setInstallmentRangeFirstDate(date);
                }}
                errorMessage={rangeFirstDateErrorMsg}
                id="multiple-first-date"
              />
            </Col>
            <Col className="min-margin multi-payment__field">
              <CurrencyInput
                title="Payment amount*"
                value={installmentRangeAmount}
                onValueChange={amount => {
                  setRangeInstallmentAmountErrorMsg('');
                  setInstallmentRangeAmount(amount);
                }}
                errorMessage={rangeInstallmentAmountErrorMsg}
              />
            </Col>
            <Col className="min-margin multi-payment__field">
              <FrequencyPicker
                title="Frequency*"
                value={installmentRangeFrequency}
                onValueChange={value => {
                  setRangeInstallmentRangeFrequencyErrorMsg('');
                  setInstallmentRangeFrequency(value);
                }}
                errorMessage={rangeInstallmentRangeFrequencyErrorMsg}
                placeHolder="Select frequency"
              />
            </Col>
            <Col className="min-margin multi-payment__field">
              <DatePicker
                title="Last payment date*"
                date={installmentRangeLastDate}
                minDate={minimumDate}
                maxDate={finalDate}
                onDateChange={date => {
                  setRangeLastDateErrorMsg('');
                  setInstallmentRangeLastDate(date);
                }}
                errorMessage={rangeLastDateErrorMsg}
                id="multiple-last-date"
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Col>
            <p className="modal__warning">{MULTIPLE_MAX_WARNING}</p>
          </Col>
          <Button
            variant="primary"
            title="Add multiple"
            onClick={() => {
              if (validateNewRange()) {
                addInstallmentRange();
                setShowMultiple(false);
                clearRange();
              }
            }}
          >
            Enter
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div>
      {installments.length ? (
        <Table className="installment-container">
          <Thead>
            <Tr>
              <Th id="installment-date-title">Payment Date*</Th>
              <Th id="installment-amount-title">Payment Amount*</Th>
              <Th>Remove</Th>
            </Tr>
          </Thead>
          <Tbody>
            {installments.map(item => (
              <Installment
                minimumDate={minimumDate}
                key={item.key}
                id={item.id}
                date={item.date}
                amount={item.amount}
                removeCallback={removeItem}
                updateInstallmentCallback={updateItem}
                dateErrorMessage={item.dateErrorMessage}
                amountErrorMessage={item.amountErrorMessage}
                sort={item.sort}
                finalDate={finalDate}
              />
            ))}
          </Tbody>
        </Table>
      ) : null}

      {hasErrors ? warning() : null}

      <h5> Add single payments</h5>

      <Row>
        <IconButton
          title="Add a Payment"
          icon={AiOutlinePlusCircle}
          variant="clear"
          onClick={addItem}
          className="blue-plus"
        />
      </Row>

      <h5>And/or add multiple payments of the same amount</h5>
      <Row>
        {!showMultiple ? (
          <div>
            {' '}
            <IconButton
              title="Add Multiple Payments"
              icon={AiOutlinePlusCircle}
              variant="clear"
              onClick={() => setShowMultiple(true)}
              className="blue-plus"
            />
          </div>
        ) : null}
      </Row>
      {showMultiple ? multiplePayments() : null}
      <br />
      <Button variant="primary" title="Reset Payments" onClick={clearItems} />
    </div>
  );
}

export default InstallmentContainer;
